import { useInitiateOauth } from 'hooks/useInitiateOauth'
import { useConnectIntegrationTrigger } from 'hooks/useConnectIntegrationTrigger/useConnectIntegrationTrigger'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useCallback } from 'react'

export interface UseInitiateOauthTriggerWithPopUpProps<T = ExternalIntegrationEnum> {
  provider: T
  accountToRefreshId?: number
  popUp: true
  switchFromIntegrationId?: number
  onInitiatedOauth: (popUp?: Window, options?: { createdIntegration?: any; switchFromIntegrationId?: number }) => void
}

export interface UseInitiateOauthTriggerWithoutPopUpProps<T = ExternalIntegrationEnum> {
  provider: T
  accountToRefreshId?: number
  popUp?: never
  switchFromIntegrationId?: number
  onInitiatedOauth?: (
    popUp?: undefined,
    options?: { createdIntegration?: any; switchFromIntegrationId?: number },
  ) => void
}

export type UseInitiateOauthTriggerProps<T = ExternalIntegrationEnum> =
  | UseInitiateOauthTriggerWithPopUpProps<T>
  | UseInitiateOauthTriggerWithoutPopUpProps<T>

export const useInitiateOauthTrigger = <T extends ExternalIntegrationEnum = ExternalIntegrationEnum>({
  provider,
  onInitiatedOauth,
  accountToRefreshId,
  popUp,
  switchFromIntegrationId,
}: UseInitiateOauthTriggerProps<T>) => {
  const { initiateOauth } = useInitiateOauth()

  const handleOAuth = useCallback(
    async (connectingProvider: ExternalIntegrationEnum, shopDomain?: string) => {
      const result = await initiateOauth(
        connectingProvider,
        accountToRefreshId,
        popUp ? '_blank' : undefined,
        popUp ? 'popup=true,width=600,height=780' : undefined,
        shopDomain,
      )
      if (popUp) {
        // Only call onInitiatedOauth if we got a valid window (not null/undefined)
        if (result) {
          onInitiatedOauth(result as Window, { switchFromIntegrationId })
        }
      } else {
        onInitiatedOauth?.(undefined, { switchFromIntegrationId })
      }
    },
    [accountToRefreshId, initiateOauth, onInitiatedOauth, popUp, switchFromIntegrationId],
  )

  // Handles providers that use custom modals to collect prerequisites
  // - WordPress: Non-OAuth, collects API credentials
  // - Shopify: OAuth-based, collects shop domain first
  const handleCustomFlowConnect = useCallback(
    (provider: ExternalIntegrationEnum, options?: { createdIntegration?: any; shopDomain?: string }) => {
      if (provider === ExternalIntegrationEnum.Shopify && options?.shopDomain) {
        // Shopify uses OAuth - initiate with collected shop domain
        handleOAuth(provider, options.shopDomain)
      } else {
        // WordPress doesn't use OAuth - integration already created
        onInitiatedOauth?.(undefined, { ...options, switchFromIntegrationId })
      }
    },
    [onInitiatedOauth, switchFromIntegrationId, handleOAuth],
  )

  const usesCustomFlow = provider === ExternalIntegrationEnum.WordPress || provider === ExternalIntegrationEnum.Shopify

  const { handleConnect } = useConnectIntegrationTrigger({
    provider,
    switchFromIntegrationId,
    onConnect: usesCustomFlow ? handleCustomFlowConnect : (provider: ExternalIntegrationEnum) => handleOAuth(provider),
  })

  return {
    handleConnect,
  }
}
